import React, { useEffect, useRef, useState } from 'react';

export const FONT = { fontFamily: "'Plus Jakarta Sans', sans-serif" };
export const MONO = { fontFamily: "'JetBrains Mono', monospace" };

// Fade-up when the element scrolls into view.
export const Reveal = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setOn(true); io.disconnect(); } }, { threshold: 0.12 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className={`transition-[opacity,transform] duration-700 ease-out ${on ? 'opacity-100 transform-none' : 'opacity-0 translate-y-8'} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

export const Eyebrow = ({ children, dark }) => (
  <p className={`text-xs uppercase tracking-[0.25em] font-semibold ${dark ? 'text-blue-300' : 'text-blue-600'}`} style={MONO}>{children}</p>
);

export const H2 = ({ children, dark, className = '' }) => (
  <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05] ${dark ? 'text-white' : 'text-slate-900'} ${className}`} style={FONT}>{children}</h2>
);

export const Section = ({ id, dark, children, className = '' }) => (
  <section id={id} data-testid={`section-${id}`} className={`relative scroll-mt-28 ${dark ? 'bg-[#060d24] text-white' : 'bg-white text-slate-900'} ${className}`}>
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">{children}</div>
  </section>
);

export const Ref = ({ children, dark }) => (
  <p className={`text-xs italic ${dark ? 'text-blue-200/60' : 'text-slate-500'}`}>{children}</p>
);

export const DataTable = ({ cols, rows, testid, dark, symbolCol = -1 }) => (
  <div className={`overflow-x-auto rounded-2xl border ${dark ? 'border-white/10 bg-white/[0.03]' : 'border-slate-200 bg-white shadow-sm'}`} data-testid={testid}>
    <table className="w-full text-left text-sm min-w-[560px]">
      <thead>
        <tr className={dark ? 'bg-white/5' : 'bg-slate-50'}>
          {cols.map((c) => <th key={c} className={`px-5 py-3 text-[11px] uppercase tracking-widest font-semibold ${dark ? 'text-blue-200' : 'text-slate-500'}`} style={MONO}>{c}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className={`border-t transition-colors ${dark ? 'border-white/5 hover:bg-white/5' : 'border-slate-100 hover:bg-blue-50/50'}`}>
            {r.map((c, j) => (
              <td key={j} className={`px-5 py-3.5 ${j === 0 ? 'font-bold' : ''} ${j === symbolCol ? 'text-xl text-blue-600 font-semibold' : ''} ${dark && j !== 0 ? 'text-blue-100/80' : ''}`} style={j > 0 && /[[\]ɑɔæəʊɝɜɹʔːˈ]/.test(String(c)) ? MONO : undefined}>{c}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
